package com.devi3east.bashbuddy.auth.ui

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.imePadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Visibility
import androidx.compose.material.icons.filled.VisibilityOff
import androidx.compose.material3.Button
import androidx.compose.material3.Checkbox
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.input.PasswordVisualTransformation
import androidx.compose.ui.text.input.VisualTransformation
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.devi3east.bashbuddy.auth.requests.SignInRequest
import com.devi3east.bashbuddy.features.app.data.AppState
import com.devi3east.bashbuddy.features.app.data.Screen
import com.devi3east.bashbuddy.footer.ui.components.FooterCard

@Composable
fun SignInScreenRoute(
    appState: AppState
) {
    val uiState by appState.authViewModel.uiState.collectAsState()
    val savedEmail = appState.appPreferences.getEmail().orEmpty()

    LaunchedEffect(Unit) {
        appState.authViewModel.clearError()
    }

    SignInScreen(
        uiState = uiState,
        savedEmail = savedEmail,
        onBack = {
            val popped = appState.navController.popBackStack()

            if (!popped) {
                appState.navController.navigate(Screen.EventsScreen.route) {
                    popUpTo(0)
                    launchSingleTop = true
                }
            }
        },
        onSignIn = { request -> appState.authViewModel.signIn(request) },
        onSwitchToSignUp = {
            appState.navController.navigate(Screen.SignUpScreen.route) {
                popUpTo(Screen.SignInScreen.route) { inclusive = true }
            }
        },
        onForgotPassword = { email -> appState.authViewModel.sendPasswordReset(email) },
        onNavigateToTerms = { appState.navController.navigate(Screen.TermsOfServiceScreen.route) },
        onNavigateToPrivacyPolicy = { appState.navController.navigate(Screen.PrivacyPolicyScreen.route) },
        onNavigateToContact = { appState.navController.navigate(Screen.ContactUsScreen.route) },
        onRememberEmail = { email ->
            appState.appPreferences.saveEmail(email)
        }
    )
}

@Composable
fun SignInScreen(
    uiState: AuthUiState,
    savedEmail: String,
    onBack: () -> Unit,
    onSignIn: (SignInRequest) -> Unit,
    onSwitchToSignUp: () -> Unit,
    onForgotPassword: (String) -> Unit,
    onNavigateToTerms: () -> Unit,
    onNavigateToPrivacyPolicy: () -> Unit,
    onNavigateToContact: () -> Unit,
    onRememberEmail: (String) -> Unit
) {
    var email by remember { mutableStateOf(savedEmail) }
    var password by remember { mutableStateOf("") }
    var passwordVisible by remember { mutableStateOf(false) }
    var showRecoveryDialog by remember { mutableStateOf(false) }
    var resetEmail by remember { mutableStateOf("") }
    var hasSubmitted by remember { mutableStateOf(false) }
    var rememberMe by remember { mutableStateOf(savedEmail.isNotEmpty()) }

    val scrollState = rememberScrollState()
    val canSubmit = email.isNotBlank() && password.length >= 8

    LaunchedEffect(uiState.errorMessage) {
        if (!uiState.errorMessage.isNullOrBlank()) {
            hasSubmitted = false
        }
    }

    if (showRecoveryDialog) {
        PasswordRecoverDialog(
            resetEmail = resetEmail,
            onEmailChange = { resetEmail = it },
            onDismiss = { showRecoveryDialog = false },
            onSubmit = { recoveryEmail ->
                onForgotPassword(recoveryEmail)
                showRecoveryDialog = false
            }
        )
    }

    Scaffold(
        containerColor = MaterialTheme.colorScheme.background
    ) { innerPadding ->
        Box(
            modifier = Modifier
                .fillMaxSize()
                .padding(innerPadding),
            contentAlignment = Alignment.Center
        ) {
            Box(
                modifier = Modifier
                    .widthIn(min = 300.dp, max = 500.dp)
                    .padding(16.dp)
            ) {
                Box(
                    modifier = Modifier
                        .clip(RoundedCornerShape(16.dp))
                        .background(MaterialTheme.colorScheme.surface)
                        .imePadding()
                ) {
                    Column(
                        modifier = Modifier
                            .padding(24.dp)
                            .verticalScroll(scrollState),
                        verticalArrangement = Arrangement.spacedBy(16.dp),
                        horizontalAlignment = Alignment.CenterHorizontally
                    ) {
                        Text(
                            text = "It’s Bash Time, Bub!",
                            style = MaterialTheme.typography.headlineMedium.copy(
                                fontWeight = FontWeight.Bold,
                                color = MaterialTheme.colorScheme.onSurface
                            ),
                            textAlign = TextAlign.Center
                        )

                        Text(
                            text = "Your next cause or event is waiting — sign in to get started!",
                            style = MaterialTheme.typography.bodyMedium.copy(
                                color = MaterialTheme.colorScheme.onSurfaceVariant,
                                lineHeight = 20.sp
                            ),
                            textAlign = TextAlign.Center,
                            modifier = Modifier.padding(horizontal = 8.dp)
                        )

                        HorizontalDivider(
                            thickness = 1.dp,
                            color = MaterialTheme.colorScheme.outlineVariant.copy(alpha = 0.3f)
                        )

                        OutlinedTextField(
                            value = email,
                            onValueChange = { input ->
                                email = input.replace(" ", "").lowercase()
                            },
                            enabled = !hasSubmitted,
                            label = { Text("Email") },
                            keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Email),
                            singleLine = true,
                            modifier = Modifier.fillMaxWidth(),
                            shape = RoundedCornerShape(12.dp)
                        )

                        OutlinedTextField(
                            value = password,
                            onValueChange = { input ->
                                password = input.replace(" ", "")
                            },
                            enabled = !hasSubmitted,
                            label = { Text("Password") },
                            visualTransformation = if (passwordVisible) VisualTransformation.None else PasswordVisualTransformation(),
                            trailingIcon = {
                                IconButton(onClick = { passwordVisible = !passwordVisible }) {
                                    Icon(
                                        imageVector = if (passwordVisible) Icons.Default.Visibility else Icons.Default.VisibilityOff,
                                        contentDescription = null
                                    )
                                }
                            },
                            keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Password),
                            singleLine = true,
                            modifier = Modifier.fillMaxWidth(),
                            shape = RoundedCornerShape(12.dp)
                        )

                        Row(
                            verticalAlignment = Alignment.CenterVertically,
                            modifier = Modifier.fillMaxWidth()
                        ) {
                            Checkbox(
                                checked = rememberMe,
                                onCheckedChange = {
                                    rememberMe = it
                                    if (!it) onRememberEmail("") // clear saved email
                                }
                            )
                            Spacer(modifier = Modifier.width(8.dp))
                            Text("Remember Me")
                        }

                        if (!uiState.errorMessage.isNullOrBlank()) {
                            Text(
                                text = uiState.errorMessage,
                                color = MaterialTheme.colorScheme.error,
                                style = MaterialTheme.typography.bodyMedium.copy(fontWeight = FontWeight.Medium),
                                textAlign = TextAlign.Center
                            )
                        }

                        Row(
                            modifier = Modifier.fillMaxWidth(),
                            horizontalArrangement = Arrangement.End,
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            TextButton(
                                onClick = {
                                    onBack()
                                }
                            ) {
                                Text(
                                    text = "Back"
                                )
                            }

                            Spacer(modifier = Modifier.widthIn(8.dp))

                            Button(
                                onClick = {
                                    hasSubmitted = true

                                    if (rememberMe) {
                                        onRememberEmail(email.trim())
                                    } else {
                                        onRememberEmail("")
                                    }

                                    onSignIn(SignInRequest(email.trim(), password.trim()))
                                },
                                enabled = canSubmit && !hasSubmitted,
                                modifier = Modifier.height(54.dp),
                                shape = RoundedCornerShape(12.dp)
                            ) {
                                if (hasSubmitted) {
                                    CircularProgressIndicator(
                                        color = MaterialTheme.colorScheme.onPrimary,
                                        strokeWidth = 2.dp,
                                        modifier = Modifier.size(22.dp)
                                    )
                                } else {
                                    Text(
                                        text = "Sign In",
                                        fontSize = 16.sp,
                                        fontWeight = FontWeight.SemiBold
                                    )
                                }
                            }
                        }

                        HorizontalDivider(
                            thickness = 1.dp,
                            color = MaterialTheme.colorScheme.outlineVariant.copy(alpha = 0.3f)
                        )

                        Column(
                            horizontalAlignment = Alignment.CenterHorizontally
                        ) {
                            TextButton(onClick = { showRecoveryDialog = true }) {
                                Text(
                                    text = "Forgot your password?",
                                    fontWeight = FontWeight.Medium,
                                    color = MaterialTheme.colorScheme.primary
                                )
                            }

                            Text(
                                text = "No account? No problem!",
                                style = MaterialTheme.typography.bodyMedium.copy(
                                    color = MaterialTheme.colorScheme.onSurfaceVariant
                                )
                            )

                            TextButton(onClick = onSwitchToSignUp) {
                                Text(
                                    text = "Click here to sign up!",
                                    fontWeight = FontWeight.Medium,
                                    color = MaterialTheme.colorScheme.primary
                                )
                            }
                        }

                        FooterCard(
                            onNavigateToTerms = { onNavigateToTerms() },
                            onNavigateToPrivacyPolicy = { onNavigateToPrivacyPolicy() },
                            onNavigateToContact = { onNavigateToContact() }
                        )
                    }
                }
            }
        }
    }
}